import { useCallback, useEffect, useState } from 'react';

import { startExtensionAuth } from '../../api/extension';
import { Button } from '../ui/button';
import { Card, CardBody, CardHeader } from '../ui/card';
import { EXTENSION_HOWTO_ID } from './ExtensionHowToCard';

interface Issued {
  code: string;
  expiresAt: number;
}

/**
 * "Подключить расширение" (§3): mints a one-time code bound to the PKCE
 * challenge the extension put in the URL. Without a challenge the code would
 * be unusable, so instead of a dead button the card says why and offers
 * «Как подключить», which jumps to the instruction on the same page.
 */
export function ConnectCodeCard({ challenge, onIssued }: { challenge: string | null; onIssued?: () => void }) {
  const [issued, setIssued] = useState<Issued | null>(null);
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!issued) return;
    const tick = () => setLeft(Math.max(0, Math.round((issued.expiresAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [issued]);

  const issue = useCallback(async () => {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const res = await startExtensionAuth(challenge);
      setIssued({ code: res.code, expiresAt: Date.now() + res.expires_in * 1000 });
      onIssued?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [challenge, onIssued]);

  const copy = useCallback(async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [issued]);

  const showHowTo = useCallback(() => {
    const target = document.getElementById(EXTENSION_HOWTO_ID);
    // HashRouter owns the URL hash, so this is a scroll, not an `href="#…"` anchor.
    target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    target?.focus?.({ preventScroll: true });
  }, []);

  return (
    <Card>
      <CardHeader className="font-medium">Подключение расширения</CardHeader>
      <CardBody className="space-y-3">
        {challenge ? (
          <Button onClick={() => void issue()} disabled={busy}>
            {busy ? 'Выдаём код…' : 'Подключить расширение'}
          </Button>
        ) : (
          <>
            <p className="text-sm">
              Сейчас нет запроса на подключение от расширения, поэтому выдать код нельзя. Запрос создаёт само
              расширение: откройте его боковую панель в Chrome и нажмите «Открыть кабинет» — эта страница откроется
              с кнопкой «Подключить расширение».
            </p>
            <Button variant="secondary" onClick={showHowTo}>
              Как подключить
            </Button>
          </>
        )}

        {issued && left > 0 && (
          <div className="space-y-2">
            <div className="select-all rounded border border-border bg-muted/40 px-4 py-3 text-center font-mono text-2xl tracking-widest">
              {issued.code}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="secondary" size="sm" onClick={() => void copy()}>
                {copied ? 'Скопировано' : 'Скопировать'}
              </Button>
              <span className="text-sm text-muted-fg">Код действует ещё {left} с</span>
            </div>
            <p className="text-sm text-muted-fg">Вставьте код в поле «Вставьте код» в панели расширения.</p>
          </div>
        )}
        {issued && left === 0 && (
          <p className="text-sm text-amber-700">Код истёк — нажмите «Подключить расширение» ещё раз.</p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </CardBody>
    </Card>
  );
}
